(() => {
  const publishedImpressions = new Set();
  const TOUCH_KEY = 'reevys:last-touch';
  const ORIGIN_KEY = 'reevys:last-origin';
  const TOUCH_TTL = 30 * 60 * 1000;

  function readTouch() {
    try {
      const value = JSON.parse(sessionStorage.getItem(TOUCH_KEY) || 'null');
      if (!value || Date.now() - value.timestamp > TOUCH_TTL) return null;
      if (value.destination) {
        const destinationPath = new URL(value.destination, location.origin).pathname;
        if (location.pathname.startsWith('/products/') && destinationPath !== location.pathname) return null;
      }
      return value;
    } catch {
      return null;
    }
  }

  function rememberTouch(data) {
    try {
      sessionStorage.setItem(TOUCH_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
    } catch {}
  }

  function readOrigin() {
    try {
      const value = JSON.parse(sessionStorage.getItem(ORIGIN_KEY) || 'null');
      if (!value || Date.now() - value.timestamp > TOUCH_TTL) return null;
      return value;
    } catch {
      return null;
    }
  }

  function rememberOrigin(data) {
    try {
      sessionStorage.setItem(ORIGIN_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
    } catch {}
  }

  function recommendationSource(intent) {
    if (intent === 'complementary') return 'complete_the_look';
    if (intent === 'related') return 'related_products';
    if (intent === 'color') return 'more_in_color';
    return null;
  }

  function sourceFor(element) {
    if (element instanceof Element) {
      const relationship = element.closest('[data-reevys-relationship-source]');
      if (relationship?.dataset.reevysRelationshipSource) return relationship.dataset.reevysRelationshipSource;
      const recommendations = element.closest('product-recommendations[data-intent]');
      const recommendation = recommendationSource(recommendations?.dataset.intent);
      if (recommendation) return recommendation;
      if (element.closest('[data-reevys-cart-look]')) return 'cart_drawer';
      if (element.closest('.reevys-cart-complete')) return 'cart_page';
      if (element.closest('[data-reevys-collection-navigator]')) return 'navigator';
      if (element.closest('predictive-search-component')) return 'predictive_search';
      if (element.closest('results-list')) return 'search';
      if (element.closest('.reevys-collection-card, .product-grid-container')) return 'collection';
    }
    if (location.pathname.startsWith('/products/')) return readTouch()?.source || 'pdp';
    return 'site';
  }

  function placementFor(element) {
    if (element instanceof Element) {
      const relationship = element.closest('[data-reevys-relationship-source]');
      if (relationship?.dataset.reevysPlacement) return relationship.dataset.reevysPlacement;
      if (relationship?.dataset.reevysRelationshipSource) return relationship.dataset.reevysRelationshipSource;
      const recommendations = element.closest('product-recommendations[data-intent]');
      const recommendation = recommendationSource(recommendations?.dataset.intent);
      if (recommendation) return recommendation;
      if (element.closest('[data-reevys-cart-look]')) return 'finish_the_look';
      if (element.closest('.reevys-cart-complete')) return 'complete_your_bag';
      if (element.closest('[data-reevys-collection-navigator]')) return 'collection_navigator';
      if (element.closest('predictive-search-component')) return 'predictive_search';
      if (element.closest('results-list')) return 'search_results';
    }
    if (location.pathname.startsWith('/products/')) return readTouch()?.placement || 'pdp';
    return null;
  }

  function productIdFor(element) {
    if (!(element instanceof Element)) return null;
    return element.closest('product-card[data-product-id], [data-product-id]')?.dataset.productId || null;
  }

  function isRecommendationSource(source) {
    return !['unknown', 'site', 'collection', 'search', 'predictive_search', 'navigator', 'pdp'].includes(source);
  }

  function publish(name, data = {}) {
    const payload = {
      ...data,
      path: location.pathname,
      search: location.search,
      timestamp: Date.now()
    };
    try {
      const result = window.Shopify?.analytics?.publish?.('reevys:' + name, payload);
      result?.catch?.(() => {});
    } catch {}
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'reevys_' + name, ...payload });
      }
    } catch {}
    try {
      document.dispatchEvent(new CustomEvent('reevys:analytics', { detail: { name, payload } }));
    } catch {}
  }

  function persistCartAttribution({ source, placement, productId, query }) {
    if (!source || source === 'site') return;
    const root = window.Shopify?.routes?.root || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          __reevys_source: source,
          __reevys_placement: placement || '',
          __reevys_product_id: productId || '',
          __reevys_query: query || '',
          __reevys_attributed_at: new Date().toISOString()
        }
      })
    }).catch(() => {});
  }

  const impressionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
      const card = entry.target;
      const productId = productIdFor(card);
      const source = sourceFor(card);
      const placement = placementFor(card);
      const key = [productId, source, placement].join('|');
      if (!productId || publishedImpressions.has(key)) return;
      publishedImpressions.add(key);
      publish('product_impression', { product_id: productId, source, placement });
      if (isRecommendationSource(source)) {
        publish('recommendation_impression', { product_id: productId, source, placement });
      }
      impressionObserver.unobserve(card);
    });
  }, { threshold: [0.35] });

  function observeProductCards(root = document) {
    if (!('IntersectionObserver' in window)) return;
    const cards = root.querySelectorAll?.('product-card[data-product-id]') || [];
    cards.forEach((card) => {
      if (card.dataset.reevysObserved === 'true') return;
      card.dataset.reevysObserved = 'true';
      impressionObserver.observe(card);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const navigatorLink = target.closest('[data-reevys-collection-navigator] a');
    if (navigatorLink) {
      publish('navigator_click', {
        destination: navigatorLink.getAttribute('href'),
        label: navigatorLink.textContent?.trim() || null,
        source: 'navigator'
      });
    }

    const collectionOriginLink = target.closest('[data-reevys-relationship-source] a[href*="/collections/"]');
    if (collectionOriginLink) {
      const originRoot = collectionOriginLink.closest('[data-reevys-relationship-source]');
      const source = originRoot?.dataset.reevysRelationshipSource || null;
      const placement = originRoot?.dataset.reevysPlacement || source;
      const destination = collectionOriginLink.getAttribute('href');
      if (source && destination) {
        rememberOrigin({ source, placement, destination });
        publish('discovery_click', {
          source,
          placement,
          destination,
          label: collectionOriginLink.textContent?.trim() || null
        });
      }
    }

    const productLink = target.closest('a[href*="/products/"]');
    if (!productLink) return;

    const productId = productIdFor(productLink);
    let source = sourceFor(productLink);
    let placement = placementFor(productLink);
    const origin = readOrigin();
    if (source === 'collection' && origin?.destination) {
      const originPath = new URL(origin.destination, location.origin).pathname;
      if (originPath === location.pathname) {
        source = origin.source || source;
        placement = origin.placement || placement;
      }
    }
    const destination = productLink.getAttribute('href');
    const query = source === 'search'
      ? new URLSearchParams(location.search).get('q')
      : source === 'predictive_search'
        ? document.querySelector('predictive-search-component input[type="search"]')?.value || null
        : null;

    rememberTouch({ productId, source, placement, destination, query });

    publish('product_click', {
      product_id: productId,
      source,
      placement,
      destination
    });

    if (isRecommendationSource(source)) {
      publish('recommendation_click', { product_id: productId, source, placement, destination });
    }
    if (source === 'search' || source === 'predictive_search') {
      publish('search_result_click', {
        product_id: productId,
        source,
        placement,
        query
      });
    }
  }, { capture: true });

  document.addEventListener('shopify:search:update', (event) => {
    const query = event.search?.query || null;
    event.promise?.then?.(({ totalCount }) => {
      publish('search_query', { query, total_results: totalCount, source: 'predictive_search' });
      if (totalCount === 0) publish('zero_result', { query, source: 'predictive_search' });
    }).catch(() => {});
  });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add') return;
    const origin = event.target instanceof Element ? event.target : null;
    const source = sourceFor(origin);
    const placement = placementFor(origin);
    const touch = readTouch();
    const productId = productIdFor(origin) || touch?.productId || null;
    const query = touch?.query || null;

    event.promise?.then?.((result) => {
      if (result?.userErrors?.length) return;
      publish('add_to_bag', {
        product_id: productId,
        source,
        placement,
        query,
        lines: event.lines?.map?.((line) => ({ merchandise_id: line.merchandiseId, quantity: line.quantity })) || []
      });
      if (isRecommendationSource(source)) {
        publish('recommendation_atc', { product_id: productId, source, placement });
      }
      persistCartAttribution({ source, placement, productId, query });
    }).catch(() => {});
  });

  const boot = () => {
    observeProductCards();

    const query = new URLSearchParams(location.search).get('q');
    if (location.pathname === '/search' && query) {
      const resultsList = document.querySelector('results-list[data-search-results-count]');
      const count = Number(resultsList?.dataset.searchResultsCount || 0);
      publish('search_query', { query, total_results: count, source: 'search' });
      if (count === 0) publish('zero_result', { query, source: 'search' });
    }

    const mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) observeProductCards(node);
        });
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  window.ReevysAnalytics = { publish };
})();